import React, { useState } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { takeSessionId } from '@renderer/utils/sessionIdSource'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { RecordingType } from '@renderer/types/configState'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { getNextPageAfterWelcome } from '@renderer/utils/navigationUtils'

const Welcome: React.FC = () => {
  const { setSessionState, uploadSessionState } = useSessionStateStore()
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { config, getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const [isSessionStarting, setIsSessionStarting] = useState(false)

  const startSession = async (mode: 'capture' | 'record'): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      const bypassIds = [
        '67e3f8d12bb1bd3dbad28011',
        '67e3f8e32bb1bd3dbad28012',
        '67e3f8f02bb1bd3dbad28013'
      ]

      // Skip credit validation for bypass projects
      if (bypassIds.includes(projectId || '')) {
        const sessionId = takeSessionId()
        const recordingType = config?.pages?.checkout?.settings?.packages?.find(
          (pkg) =>
            pkg.type === 'recording' ||
            pkg.type === 'strip_live' ||
            pkg.type === 'strip_story' ||
            pkg.type === 'session_recording'
        )?.type as RecordingType

        setSessionState({
          id: sessionId,
          projectId,
          mode,
          timeline: {
            startTime: new Date(),
            activityLogs: [
              {
                page: 'welcome',
                type: 'session',
                action: 'start',
                details: { mode },
                timestamp: new Date()
              }
            ]
          },
          userPreferences: {
            useGIF: getPageSettings('capture')?.gifEnabled,
            useRecording: getPageSettings('capture')?.recordingEnabled,
            useVideo: getPageSettings('record')?.enabled,
            recordingType
          },
          gallery: {
            id: `${9999999999999 - Date.now()}-${sessionId}`
          }
        })
        const nextPage = getNextPageAfterWelcome(
          config,
          welcomeSettings?.startWithPayment || false
        )
        uploadSessionState()
        navigate(`/${projectId}/${nextPage}`)
        return
      }

      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing

      const sessionId = takeSessionId()

      // Get recording package to determine type
      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      setSessionState({
        id: sessionId,
        projectId,
        mode,
        timeline: {
          startTime: new Date(),
          activityLogs: [
            {
              page: 'welcome',
              type: 'session',
              action: 'start',
              details: { mode },
              timestamp: new Date()
            }
          ]
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        }
      })
      // Navigate based on dashboard Navigation settings (startWithPayment / applyAfterCapture)
      const nextPage = getNextPageAfterWelcome(
        config,
        welcomeSettings?.startWithPayment || false
      )
      uploadSessionState()
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Failed to check credits. Please try again.')
    } finally {
      setIsSessionStarting(false)
    }
  }

  // Handle button actions from ComponentMapper
  // const handleButtonAction = (action: string): void => {
  //   switch (action) {
  //     case 'capture':
  //       startSession('capture')
  //       break
  //     case 'record':
  //       startSession('record')
  //       break
  //     default:
  //       console.warn('Unknown button action:', action)
  //   }
  // }

  // Call all hooks at the top level
  const cachedWelcomeBg = useCachedBackgroundImage('/assets/photophoria/photophoria-welcome.webp')
  const cachedWelcomeButton = useCachedImage('/assets/photophoria/photophoria-welcome-button.webp')

  // Using components from configuration instead of hardcoded elements
  return (
    <div
      className="w-full h-full relative"
      style={{
        backgroundImage: `url(${cachedWelcomeBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden'
      }}
    >
      <button
        onClick={() => startSession('capture')}
        aria-label="Start Session"
        className="bottom-[180px] absolute left-[650px] transition-all duration-200 ease-in-out transform hover:scale-110"
      >
        <img
          src={cachedWelcomeButton}
          alt="Start Session"
          className="object-contain drop-shadow-2xl w-[420px]"
        />
      </button>
    </div>
  )
}

export default Welcome
